import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { FloppyDisk, PaperPlaneTilt, X } from "@phosphor-icons/react";

/**
 * Phase 11 Beat 1 — GL/Manager Evaluation full-form modal.
 *
 * Renders every Excel section as a smart editable card, pre-filled from the
 * candidate's dossier + profile. GL can edit any field, save draft as many
 * times as needed, then submit. Submit marks the candidate as Marketing Ready
 * (via `marks_marketing_ready` flag) and promotes non-manual fields to the
 * profile. Historical evaluations are never overwritten — every submit
 * creates a new evaluation row.
 *
 * Endpoints:
 *   PATCH /api/evaluations/{eid}/full-form          — save draft
 *   POST  /api/evaluations/{eid}/submit-full-form   — final submit
 */
const SECTIONS = [
  { key: "personal_preferences",     label: "Personal & Location Preferences" },
  { key: "job_search_status",        label: "Job Search Status" },
  { key: "role_industry_preferences", label: "Role & Industry Preferences" },
  { key: "skills_and_tools",         label: "Skills & Tools" },
  { key: "visa_and_sponsorship",     label: "Visa & Sponsorship" },
  { key: "career_development",       label: "Career Development Needs" },
  { key: "communication_and_marketing", label: "Communication & Marketing" },
  { key: "program_expectations",     label: "Program Expectations" },
];

export default function EvaluationFullFormModal({ evaluationId, onClose, onSubmitted }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [markMR, setMarkMR] = useState(false);
  const [summary, setSummary] = useState("");
  const [scores, setScores] = useState({ technical: 7, communication: 7, marketing_readiness: 7 });

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/evaluations/${evaluationId}`);
      setForm(r.data.form || {});
      setSummary((r.data.form || {}).evaluator_summary || "");
      setScores((r.data.form || {}).scores || { technical: 7, communication: 7, marketing_readiness: 7 });
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail) || "Load failed"); }
  }, [evaluationId]);
  useEffect(() => { load(); }, [load]);

  const updateSectionField = (sectionKey, field, value) => {
    setForm((f) => ({ ...(f || {}), [sectionKey]: { ...((f || {})[sectionKey] || {}), [field]: value } }));
  };

  const payload = () => ({ ...form, evaluator_summary: summary, scores });

  const saveDraft = async () => {
    setBusy(true);
    try {
      await api.patch(`/evaluations/${evaluationId}/full-form`, payload());
      toast.success("Draft saved");
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (!window.confirm(markMR
        ? "Submit this evaluation AND mark candidate as Marketing Ready? This creates a permanent historical record."
        : "Submit this evaluation? This creates a permanent historical record.")) return;
    setBusy(true);
    try {
      await api.post(`/evaluations/${evaluationId}/submit-full-form`, {
        ...payload(),
        marks_marketing_ready: markMR,
      });
      toast.success(markMR ? "Evaluation submitted — candidate is now Marketing Ready!" : "Evaluation submitted");
      onSubmitted && onSubmitted();
      onClose();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail) || "Submit failed"); }
    finally { setBusy(false); }
  };

  if (form === null) return null;

  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" data-testid="eval-full-form-modal">
      <div className="bg-white border border-slate-200 rounded-xl w-full max-w-4xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-slate-100 flex-shrink-0">
          <div>
            <h3 className="font-semibold text-lg">Group Lead Evaluation Form</h3>
            <p className="text-xs text-slate-500">Pre-filled from candidate dossier. Edit any field, then submit to promote validated info to their profile.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-3">
          {SECTIONS.map((s) => (
            <SectionCard key={s.key} label={s.label} testid={`eval-section-${s.key}`}>
              <SectionEditor sectionKey={s.key} data={form[s.key] || {}}
                             onChange={(field, value) => updateSectionField(s.key, field, value)} />
            </SectionCard>
          ))}

          {/* Scores + Marketing Ready — GL-only fields */}
          <SectionCard label="Scores (GL only)" testid="eval-section-scores">
            <div className="grid grid-cols-3 gap-3">
              <ScoreInput label="Technical (1-10)" value={scores.technical} onChange={(v) => setScores({ ...scores, technical: Number(v) })} />
              <ScoreInput label="Communication (1-10)" value={scores.communication} onChange={(v) => setScores({ ...scores, communication: Number(v) })} />
              <ScoreInput label="Marketing Readiness (1-10)" value={scores.marketing_readiness} onChange={(v) => setScores({ ...scores, marketing_readiness: Number(v) })} />
            </div>
          </SectionCard>

          <SectionCard label="Evaluator Summary" testid="eval-section-summary">
            <textarea rows={3} value={summary} onChange={(e) => setSummary(e.target.value)}
                      data-testid="eval-summary"
                      placeholder="Key strengths, gaps, and next steps…"
                      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm resize-none" />
          </SectionCard>

          <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-md p-3">
            <input type="checkbox" id="mr" checked={markMR} onChange={(e) => setMarkMR(e.target.checked)}
                   data-testid="eval-mark-mr" className="w-4 h-4" />
            <label htmlFor="mr" className="text-sm">
              <b>Mark candidate as Marketing Ready</b>
              <span className="text-xs text-emerald-800 block">Sets candidate.marketing_ready=true and promotes validated fields (skills, visa, role, salary, notice_period) to their profile. Historical evaluations remain immutable.</span>
            </label>
          </div>
        </div>

        <div className="p-5 border-t border-slate-100 flex items-center justify-end gap-2 flex-shrink-0">
          <button onClick={onClose} className="text-sm border border-slate-200 rounded-md px-3 py-2 hover:bg-slate-50" data-testid="eval-cancel">Cancel</button>
          <button onClick={saveDraft} disabled={busy}
                  data-testid="eval-save-draft"
                  className="text-sm border border-slate-200 rounded-md px-3 py-2 inline-flex items-center gap-1.5 hover:bg-slate-100 disabled:opacity-50">
            <FloppyDisk size={14} /> Save Draft
          </button>
          <button onClick={submit} disabled={busy}
                  data-testid="eval-submit"
                  className="text-sm bg-sky-600 hover:bg-sky-700 text-white rounded-md px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-50">
            <PaperPlaneTilt size={14} /> Submit
          </button>
        </div>
      </div>
    </div>
  );
}

function SectionCard({ label, testid, children }) {
  return (
    <div className="border border-slate-200 rounded-md p-3" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-slate-500 mb-2 font-medium">{label}</div>
      {children}
    </div>
  );
}
function SectionEditor({ sectionKey, data, onChange }) {
  const entries = Object.entries(data || {});
  if (entries.length === 0) {
    return <p className="text-xs text-slate-400">No pre-filled data — add fields as needed.</p>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
      {entries.map(([k, v]) => (
        <label key={k} className="text-xs text-slate-500 block space-y-1">
          <span className="capitalize">{k.replace(/_/g, " ")}</span>
          {Array.isArray(v) ? (
            <input value={v.join(", ")} onChange={(e) => onChange(k, e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                   className="w-full border border-slate-200 rounded-md px-3 py-1.5 text-sm" />
          ) : typeof v === "boolean" ? (
            <button type="button" onClick={() => onChange(k, !v)}
                    className={`text-xs px-3 py-1.5 rounded-full border ${v ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-slate-50 border-slate-200 text-slate-500"}`}>
              {v ? "Yes" : "No"}
            </button>
          ) : (
            <input value={v ?? ""} onChange={(e) => onChange(k, e.target.value)}
                   className="w-full border border-slate-200 rounded-md px-3 py-1.5 text-sm" />
          )}
        </label>
      ))}
    </div>
  );
}
function ScoreInput({ label, value, onChange }) {
  return (
    <label className="text-xs text-slate-500 block space-y-1">
      <span>{label}</span>
      <input type="number" min={1} max={10} value={value ?? ""}
             onChange={(e) => onChange(e.target.value)}
             className="w-full border border-slate-200 rounded-md px-3 py-1.5 text-sm" />
    </label>
  );
}
